import React, { ForwardedRef, forwardRef } from 'react';
import { useFelaEnhanced } from '@ackee/fela';
import { FormattedMessage } from 'react-intl';

import { useIntl } from 'modules/ui/dependencies';
import { routes } from 'config/routes';
import * as UI from 'modules/ui';

import * as felaRules from './CaseStudies.rules';

import ImageCard from '../components/ImageCard';

import { homePageReferences, type HomePageReferencesType } from './constants';

import { manageHover } from './utils/manageHover';

const { ContainerType, LazyLoadContainer, DynamicContainer, TitleTypes, Text, Title, LocalizedLink } = UI;

interface CaseStudiesProps {
    lazyLoaded?: boolean;
}

const CaseStudies = forwardRef(({ lazyLoaded = false }: CaseStudiesProps, ref: ForwardedRef<HTMLElement>) => {
    const { styles, rules } = useFelaEnhanced(felaRules);
    const { locale } = useIntl();

    const content = homePageReferences[locale as keyof HomePageReferencesType];
    return (
        <DynamicContainer
            ref={ref}
            as={ContainerType.SECTION}
            extend={{ container: rules.extendContainer }}
            isFluid
            columns={12}
        >
            <LazyLoadContainer lazyLoaded={lazyLoaded}>
                {/* TITLE and TOP RIGHT SVG */}
                <div className={styles.titleContainer}>
                    {/* @ts-expect-error */}
                    <Text columns={12} type={UI.TextTypes.SECTION} extend={{ text: rules.mb0 }}>
                        <FormattedMessage id="homepage.caseStudies.navigation.title" />
                    </Text>
                    <svg width="226" height="23" viewBox="0 0 226 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M226 21.7404H78.6006L57.86 1H0"
                            stroke="black"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                        />
                    </svg>
                </div>

                {/* TOP SECTION */}
                <DynamicContainer
                    type={ContainerType.GRID}
                    columns={12}
                    extend={{ container: rules.extendSectionContainer }}
                >
                    <DynamicContainer
                        type={ContainerType.BLOCK}
                        columns={6}
                        xsColumns={12}
                        extend={{ container: rules.overflowContainer }}
                    >
                        {/* @ts-expect-error */}
                        <Title as={TitleTypes.H2} type={TitleTypes.H3} extend={{ title: rules.sectionTitle }}>
                            <FormattedMessage id="homepage.caseStudies.navigation.subtitle" />
                        </Title>

                        <div className={styles.topLeftContainer} onMouseOver={() => manageHover('gif0')}>
                            <ImageCard {...content[0]} />
                        </div>
                        <div className={`dropShadowGif ${styles.dropShadow}`}>
                            <img
                                src={content[0].shadow}
                                id="gif0"
                                alt="Pixelated Shadow Image"
                                style={{ width: '110%', imageRendering: 'pixelated' }}
                            />
                        </div>
                    </DynamicContainer>

                    <DynamicContainer
                        type={ContainerType.BLOCK}
                        startAt="8"
                        columns={5}
                        mdColumns={12}
                        extend={{ container: rules.extendImgContainer }}
                    >
                        <div
                            className={`${styles.topRightContainer} entityContainer`}
                            onMouseOver={() => manageHover('gif1')}
                        >
                            <ImageCard {...content[1]} />
                        </div>
                        <div className={`dropShadowGif ${styles.dropShadow}`}>
                            <img
                                src={content[1].shadow}
                                id="gif1"
                                alt="Pixelated Shadow Image"
                                style={{ width: '110%', imageRendering: 'pixelated' }}
                            />
                        </div>
                    </DynamicContainer>
                </DynamicContainer>

                {/* MID SECTION */}
                <div className={styles.timelineContainer} onMouseOver={() => manageHover('gif2')}>
                    <ImageCard {...content[2]} extend={rules.imgTimeline} />

                    <div className={`dropShadowGif ${styles.dropShadow}`}>
                        <img
                            src={content[2].shadow}
                            id="gif2"
                            alt="Pixelated Shadow Image"
                            style={{ width: '103%', imageRendering: 'pixelated' }}
                        />
                    </div>
                </div>

                {/* BOTTOM SECTION */}
                <DynamicContainer
                    type={ContainerType.GRID}
                    columns={12}
                    extend={{ container: rules.extendSectionContainer }}
                >
                    <DynamicContainer
                        type={ContainerType.BLOCK}
                        columns={6}
                        xsColumns={12}
                        extend={{ container: rules.extendImgContainer }}
                    >
                        <div className={styles.bottomLeftContainer} onMouseOver={() => manageHover('gif3')}>
                            <ImageCard {...content[3]} />
                            <div className={`dropShadowGif ${styles.dropShadow}`}>
                                <img
                                    src={content[3].shadow}
                                    id="gif3"
                                    alt="Pixelated Shadow Image"
                                    style={{ width: '103%', imageRendering: 'pixelated' }}
                                />
                            </div>
                        </div>
                    </DynamicContainer>
                    <DynamicContainer
                        type={ContainerType.BLOCK}
                        columns={5}
                        startAt="8"
                        xsColumns={12}
                        extend={{ container: rules.extendBottomRightImgContainer }}
                    >
                        <div className={styles.bottomRightContainer} onMouseOver={() => manageHover('gif4')}>
                            <ImageCard {...content[4]} />
                            <div className={`dropShadowGif ${styles.dropShadow}`}>
                                <img
                                    src={content[4].shadow}
                                    id="gif4"
                                    alt="Pixelated Shadow Image"
                                    style={{ width: '103%', imageRendering: 'pixelated' }}
                                />
                            </div>
                        </div>
                    </DynamicContainer>
                </DynamicContainer>

                <div className={styles.bottomSection}>
                    <svg width="542" height="29" viewBox="0 0 542 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M20.5999 0.999941L46.7899 27.1899L542 27.1899"
                            stroke="black"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M27.1899 27.1899L0.999939 0.999941"
                            stroke="black"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                        />
                    </svg>
                    <span>
                        {/* <FormattedMessageWithLinks id="homepage.caseStudies.moreButton" /> */}
                        <LocalizedLink
                            eventId="contact-bottom"
                            to={routes['/references']}
                            style={{ textUnderlineOffset: '5 px' }}
                        >
                            {/* @ts-expect-error */}
                            <Title as={TitleTypes.H5} extend={{ title: rules.extendLinkTitle }}>
                                <FormattedMessage id="homepage.caseStudies.moreButton" />
                            </Title>
                        </LocalizedLink>
                    </span>
                </div>
            </LazyLoadContainer>
        </DynamicContainer>
    );
});

export default CaseStudies;
